import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { MapPin, Navigation, Phone, MessageCircle } from "lucide-react";
import { LOCATIONS, embedSrc, whatsappLink } from "../data/site";
import { useLang } from "../context/LanguageContext";

const LOCATIONS_BG = "/images/gallery/fb-town-street.jpg";

export default function Locations() {
  const { t } = useLang();
  const labels = [t.locations.main, t.locations.second];

  return (
    <div className="App">
      <Navbar />

      {/* Hero */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={LOCATIONS_BG} alt="San Juan del Sur street" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-blue/90 to-brand-sky/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <span className="inline-flex items-center gap-2 text-white/90 font-heading font-bold uppercase tracking-[0.2em] text-sm">
            <MapPin size={16} /> {t.locations.tag}
          </span>
          <h1 data-testid="locations-title" className="font-heading font-black text-4xl sm:text-5xl mt-3 max-w-2xl">{t.locations.title}</h1>
          <p className="text-white/85 mt-4 max-w-xl">{t.locations.subtitle}</p>
        </div>
      </section>

      {/* Location cards */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-10 md:grid-cols-2">
          {LOCATIONS.map((loc, i) => (
            <Reveal key={loc.id} delay={i * 0.1}>
              <div
                data-testid={`location-card-${loc.id}`}
                className="bg-white border border-slate-100 rounded-3xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow h-full flex flex-col"
              >
                <div className="aspect-[16/10] w-full bg-slate-100">
                  <iframe
                    title={`${loc.name} map`}
                    data-testid={`location-map-${loc.id}`}
                    src={embedSrc(loc.query)}
                    className="w-full h-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>

                <div className="p-6 flex flex-col flex-1">
                  <span className="text-brand-orange font-heading font-bold uppercase tracking-[0.15em] text-xs">{labels[i]}</span>
                  <h2 className="font-heading font-black text-2xl text-brand-gray mt-1">{loc.name}</h2>
                  <p className="flex items-start gap-2 text-slate-500 text-sm mt-3">
                    <MapPin size={18} className="text-brand-blue shrink-0 mt-0.5" /> {loc.address}
                  </p>

                  <div className="mt-6 flex flex-col sm:flex-row gap-3 mt-auto pt-4">
                    <a
                      href={loc.directions}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`location-directions-${loc.id}`}
                      className="flex-1 inline-flex justify-center items-center gap-2 bg-brand-blue hover:bg-brand-blue/90 text-white font-heading font-bold py-3 px-5 rounded-full transition-colors"
                    >
                      <Navigation size={18} /> {t.locations.getDirections}
                    </a>
                    <a
                      href={`tel:${loc.phone}`}
                      data-testid={`location-call-${loc.id}`}
                      className="inline-flex justify-center items-center gap-2 border border-slate-200 hover:border-brand-blue text-brand-gray hover:text-brand-blue font-heading font-bold py-3 px-5 rounded-full transition-colors"
                    >
                      <Phone size={18} /> {t.locations.call}
                    </a>
                    <a
                      href={whatsappLink(`Hola! ${loc.name} — ${loc.address}`)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`location-whatsapp-${loc.id}`}
                      className="inline-flex justify-center items-center gap-2 border border-slate-200 hover:border-brand-orange text-brand-gray hover:text-brand-orange font-heading font-bold py-3 px-5 rounded-full transition-colors"
                    >
                      <MessageCircle size={18} /> {t.locations.whatsapp}
                    </a>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
